/**
 * Shared seen-batch tracker for the PC surfaces (spec
 * 2026-08-16-first-light-design.md, D5). Both hosts call this from their
 * "fresh notifications rendered while visible" effect; keeping the whole
 * split-and-count here (not just the predicate) means the organic metrics and
 * the First Light delivery metric cannot drift between extension and web.
 *
 * Fail-open lives HERE, not at call sites — analytics must never break
 * rendering (event-spec rule 8), and a host that forgets its own try/catch
 * must still be safe.
 */

import type { StorageAdapter } from "../storage/adapter";
import type { ReflectoAnalytics } from "./client";
import { splitSeenBatch } from "./firstLight";
import { bumpCounter, captureOnce } from "./state";

export async function trackSeenBatch(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics,
  items: Array<{ appPackage: string }>,
  now = Date.now()
): Promise<void> {
  try {
    const { firstLight, organic } = splitSeenBatch(items);
    if (firstLight.length > 0) {
      await captureOnce(adapter, analytics, "first_light_received");
    }
    if (organic.length === 0) return;
    await bumpCounter(adapter, "notifications_seen_count", organic.length, now);
    const { analyticsState, pairAutoNameResult } = await adapter.get(
      "analyticsState",
      "pairAutoNameResult"
    );
    const pairedAt = analyticsState?.pairedAt ?? pairAutoNameResult?.createdAt;
    await captureOnce(adapter, analytics, "first_notification_seen", {
      hours_since_pairing:
        pairedAt === undefined ? undefined : Math.round(((now - pairedAt) / 3_600_000) * 10) / 10,
    });
  } catch {
    // fail open
  }
}
